import { AxiosResponse } from 'axios';
import {
    REPORT_ERROR_CODE,
    sanitizeReportErrorCode,
} from 'util/reportErrorMessage';

const isGzipCompressed = async (blob: Blob): Promise<boolean> => {
    try {
        const header = await blob.slice(0, 2).arrayBuffer();
        const bytes = new Uint8Array(header);
        return bytes[0] === 0x1f && bytes[1] === 0x8b;
    } catch {
        return false;
    }
};

const decompressGzip = async (blob: Blob): Promise<Blob> => {
    try {
        const stream = blob.stream();
        const decompressedStream = stream.pipeThrough(
            new DecompressionStream('gzip')
        );
        const response = new Response(decompressedStream);
        return await response.blob();
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error decompressing GZIP data:', error);
        return blob;
    }
};

const isZipLike = async (blob: Blob): Promise<boolean> => {
    const header = await blob.slice(0, 2).arrayBuffer();
    const b = new Uint8Array(header);
    return b[0] === 0x50 && b[1] === 0x4b;
};

export const resolveReportFilename = (
    res: AxiosResponse,
    fallback: string
): string => {
    const disposition = res.headers['content-disposition'];
    if (!disposition || typeof disposition !== 'string') {
        return fallback;
    }
    const star = disposition.match(/filename\*=(?:UTF-8'')?([^;\n]+)/i);
    if (star?.[1]) {
        try {
            return decodeURIComponent(
                star[1].trim().replace(/^["']|["']$/g, '')
            );
        } catch {
            return star[1].trim().replace(/^["']|["']$/g, '');
        }
    }
    const plain = disposition.match(/filename=([^;\n]+)/i);
    if (plain?.[1]) {
        return plain[1].trim().replace(/^["']|["']$/g, '');
    }
    return fallback;
};

export const getReportBlobValidationError = async (
    blob: Blob
): Promise<string | undefined> => {
    if (blob.size === 0) {
        return 'empty';
    }
    if (await isZipLike(blob)) {
        return undefined;
    }
    if (blob.size > 8192) {
        return undefined;
    }
    const text = (await blob.slice(0, 8192).text()).trim();
    if (!text) {
        return 'empty';
    }
    if (text.startsWith('{') || text.startsWith('[')) {
        try {
            const j = JSON.parse(text) as {
                message?: string;
                error?: string;
                msg?: string;
            };
            return j.message ?? j.error ?? j.msg ?? text.slice(0, 500);
        } catch {
            return text.slice(0, 500);
        }
    }
    if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
        return text.slice(0, 200);
    }
    return undefined;
};

export const parseErrorMessageFromBlob = async (
    blob: Blob
): Promise<string> => {
    const direct = await getReportBlobValidationError(blob);
    if (direct && direct !== 'empty') {
        return direct;
    }
    try {
        const t = await blob.text();
        const trim = t.trim();
        if (trim.startsWith('{')) {
            const j = JSON.parse(trim) as {
                message?: string;
                error?: string;
                msg?: string;
            };
            return j.message ?? j.error ?? j.msg ?? trim.slice(0, 500);
        }
        return trim.slice(0, 500) || 'Request failed';
    } catch {
        return 'Request failed';
    }
};

export const downloadReport = async (
    res: AxiosResponse,
    fallbackFilename = 'report.xlsx'
): Promise<void> => {
    const filename = resolveReportFilename(res, fallbackFilename);
    let blob = new Blob([res.data]);
    const isCompressed = await isGzipCompressed(blob);
    if (isCompressed) {
        blob = await decompressGzip(blob);
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export type BlobReportHandleResult = {
    error: string | null;
    statusCode: number;
    truncated?: boolean;
    downloadedFilename?: string;
};

export type HandleBlobReportOptions = {
    fallbackFilename?: string;
    limit502Message?: string;
    non200Error?: string;
};

export const handleBlobReportResponse = async (
    res: AxiosResponse<Blob> | undefined | null,
    options: HandleBlobReportOptions = {}
): Promise<BlobReportHandleResult> => {
    const fallbackFilename = options.fallbackFilename ?? 'report.xlsx';

    if (!res || typeof res.status !== 'number') {
        return { error: REPORT_ERROR_CODE.NETWORK, statusCode: 0 };
    }

    if (res.status !== 200) {
        const msg = await parseErrorMessageFromBlob(
            new Blob([res.data as BlobPart])
        );
        if (res.status === 502 && options.limit502Message) {
            return {
                error: msg.includes('50000') ? msg : options.limit502Message,
                statusCode: res.status,
            };
        }
        if (options.non200Error) {
            return { error: options.non200Error, statusCode: res.status };
        }
        return { error: msg, statusCode: res.status };
    }

    const blob = new Blob([res.data as BlobPart]);
    const validation = await getReportBlobValidationError(blob);
    if (validation === 'empty') {
        return {
            error: REPORT_ERROR_CODE.EMPTY,
            statusCode: res.status,
        };
    }
    if (validation && validation !== 'empty') {
        return {
            error: sanitizeReportErrorCode(validation),
            statusCode: res.status,
        };
    }

    const truncatedRaw =
        res.headers['x-report-truncated'] ?? res.headers['X-Report-Truncated'];
    const truncated = String(truncatedRaw).toLowerCase() === 'true';

    const filename = resolveReportFilename(res, fallbackFilename);
    await downloadReport(res, fallbackFilename);

    return {
        error: null,
        statusCode: res.status,
        truncated,
        downloadedFilename: filename,
    };
};
